import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../../../../redux/store";
import { updateConfig } from "./posConfigSlice";
import { POSSalesModeItem, POSDisplayName } from "./constants";
import { DraggableCardList } from "./DraggableCardList";
import { getSaleModeIcon } from "./posIconUtils";
import { useIsItemDisabledInGeneral } from "./useIsItemDisabledInGeneral";

export function SalesMode() {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const items = useSelector(
        (state: RootState) => state.posConfig.config.SalesMode.items
    );
    const isItemDisabledInGeneral = useIsItemDisabledInGeneral();
    const currentLang = (i18n.language || "fr").toUpperCase() as keyof POSDisplayName;
    const getLocalizedTitle = React.useCallback(
        (item: POSSalesModeItem) =>
            item.displayName[currentLang] || item.displayName.FR || item.name,
        [currentLang]
    );

    const handleToggle = React.useCallback(
        (index: number) => {
            const item = items[index];
            if (isItemDisabledInGeneral(item.id)) return;
            const activeCount = items.filter(
                (it: POSSalesModeItem) => it.active
            ).length;
            if (item.isFavorite && item.active) return;
            if (item.active && activeCount <= 1) return;

            const newItems = items.map((it: POSSalesModeItem, i: number) =>
                i === index ? { ...it, active: !it.active } : { ...it }
            );
            dispatch(
                updateConfig({ path: ["SalesMode", "items"], value: newItems })
            );
        },
        [items, dispatch, isItemDisabledInGeneral]
    );

    const isToggleDisabled = React.useCallback(
        (item: POSSalesModeItem) =>
            isItemDisabledInGeneral(item.id) ||
            Boolean(item.isFavorite && item.active),
        [isItemDisabledInGeneral]
    );

    const handleReorder = React.useCallback(
        (newItems: POSSalesModeItem[]) => {
            dispatch(
                updateConfig({ path: ["SalesMode", "items"], value: newItems })
            );
        },
        [dispatch]
    );

    return (
        <div className="h-100 pos-tab-layout__clz">
            <div>
                <div className="d-flex align-items-center cursor__clz pos-section-header__clz">
                    <div className="pos-section-title__clz">
                        {t("Sales methods choice")}
                    </div>
                </div>
            </div>

            <DraggableCardList
                droppableId="salesMode"
                items={items}
                getTitle={getLocalizedTitle}
                getIcon={getSaleModeIcon}
                isToggleDisabled={isToggleDisabled}
                isGreyedOut={(item: POSSalesModeItem) =>
                    isItemDisabledInGeneral(item.id)
                }
                onReorder={handleReorder}
                onToggle={handleToggle}
                testIdPrefix="sales-mode"
            />
        </div>
    );
}
